/**
 * Internal Review PDF Document
 * 
 * CFO-facing financial summary for the finance team.
 * Shows comprehensive project cost analysis, per-structure breakdowns,
 * profitability metrics, efficiency ratios, and risk indicators.
 * 
 * Access: admin/manager only
 */

import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from '@react-pdf/renderer';
import { ProposalNodeSet, GlobalCosts, type ProposalCostingModel, type DesignEstimatingData } from '../../types';
import { applyProfitMargin, getEffectiveProjectOverheadPercent, countGlobalCostShareEligible, usesGlobalStructureCosting } from '../../utils/proposalCalculations';
import { speciesProductionAuditLines, speciesTimberAuditLines } from '../../utils/proposalSpeciesProduction';
import { listInstallEquipmentAuditLines } from '../../utils/proposalInstallEquipment';
import {
    estimatingRaw,
    listEstimatingAuditLines,
    structureDesignHours,
    type DesignRateInputs,
} from '../../utils/proposalDesignBreakdown';
import { computeExclusiveStaffEconomics } from '../../utils/proposalExclusiveEconomics';
import { listExclusiveStructures, structureIsExclusive } from '../../utils/proposalExclusiveGroups';
import { getInstallLaborLines } from '../../utils/proposalInstallLabor';
import {
    listInstallLaborReviewLines,
    listJoineryReviewLines,
    listMaterialsHardwareReviewLines,
    listTravelReviewLines,
    materialsOtherResidual,
    reviewShopBoardFeet,
    reviewShopBoardFeetLabel,
    type ReviewJoineryPerson,
    type ReviewTextLine,
} from '../../utils/internalReviewPdfLines';

// ============================================================================
// TYPES
// ============================================================================

export interface InternalReviewData {
    // Project info
    clientName: string;
    projectTitle: string;
    preparedBy?: string;
    proposalDate: Date;
    dealId?: string;
    status?: string;

    // Financial data
    nodeSets: ProposalNodeSet[];
    profitMargin: number;
    globalCosts?: GlobalCosts;

    // Kit info
    isKitProposal?: boolean;
    activeSpecies?: string;
    sourceKitName?: string;
    costingModel?: ProposalCostingModel;
    designEstimating?: DesignEstimatingData | null;
    rates?: DesignRateInputs | null;
    /** CRM names/titles for joinery user ids. Omit a name rather than inventing one. */
    joineryPeople?: ReviewJoineryPerson[];
}

// ============================================================================
// STYLES
// ============================================================================

const s = StyleSheet.create({
    page: {
        padding: 30,
        paddingBottom: 46,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#ffffff',
    },
    // Header
    header: {
        marginBottom: 15,
        paddingBottom: 10,
        borderBottomWidth: 2,
        borderBottomColor: '#1e3a2f',
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1e3a2f',
        marginBottom: 2,
    },
    headerSubtitle: {
        fontSize: 10,
        color: '#475569',
    },
    headerMeta: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 6,
        fontSize: 8,
        color: '#64748b',
    },
    // Confidential banner
    confidential: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fca5a5',
        borderRadius: 3,
        padding: 6,
        marginBottom: 12,
        flexDirection: 'row',
        alignItems: 'center',
    },
    confidentialText: {
        fontSize: 8,
        color: '#991b1b',
        fontWeight: 'bold',
    },
    // Section
    sectionTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#1e3a2f',
        marginTop: 14,
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#d1d5db',
    },
    // Table styles
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1e3a2f',
        paddingVertical: 4,
        paddingHorizontal: 6,
        borderRadius: 2,
        marginBottom: 1,
    },
    tableHeaderText: {
        color: '#ffffff',
        fontSize: 8,
        fontWeight: 'bold',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        paddingHorizontal: 6,
        borderBottomWidth: 0.5,
        borderBottomColor: '#e5e7eb',
    },
    tableRowAlt: {
        flexDirection: 'row',
        paddingVertical: 3,
        paddingHorizontal: 6,
        borderBottomWidth: 0.5,
        borderBottomColor: '#e5e7eb',
        backgroundColor: '#f8fafc',
    },
    tableRowTotal: {
        flexDirection: 'row',
        paddingVertical: 5,
        paddingHorizontal: 6,
        backgroundColor: '#1e3a2f',
        borderRadius: 2,
        marginTop: 1,
    },
    // Summary boxes
    summaryRow: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: 10,
    },
    summaryBox: {
        flex: 1,
        backgroundColor: '#f8fafc',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 4,
        padding: 8,
    },
    summaryBoxHighlight: {
        flex: 1,
        backgroundColor: '#1e3a2f',
        borderRadius: 4,
        padding: 8,
    },
    summaryLabel: {
        fontSize: 7,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 2,
    },
    summaryLabelWhite: {
        fontSize: 7,
        color: 'rgba(255,255,255,0.7)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 2,
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#1e293b',
    },
    summaryValueWhite: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#ffffff',
    },
    summarySubtext: {
        fontSize: 7,
        color: '#94a3b8',
        marginTop: 1,
    },
    summarySubtextWhite: {
        fontSize: 7,
        color: 'rgba(255,255,255,0.6)',
        marginTop: 1,
    },
    // Metric cards
    metricGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 10,
    },
    metricCard: {
        width: '23%',
        backgroundColor: '#f8fafc',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 3,
        padding: 6,
    },
    metricLabel: {
        fontSize: 7,
        color: '#64748b',
        marginBottom: 2,
    },
    metricValue: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#1e293b',
    },
    metricSubtext: {
        fontSize: 6,
        color: '#94a3b8',
        marginTop: 1,
    },
    // Structure detail
    structureBox: {
        marginBottom: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    structureHeader: {
        backgroundColor: '#f1f5f9',
        paddingVertical: 5,
        paddingHorizontal: 8,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    structureName: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#1e293b',
    },
    structureQty: {
        fontSize: 8,
        color: '#64748b',
    },
    structureBadgeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 4,
    },
    /** View-based pills — background on Text alone is unreliable in some PDF viewers */
    badgePillAmber: {
        backgroundColor: '#fffbeb',
        borderWidth: 1,
        borderColor: '#fcd34d',
        borderRadius: 2,
        paddingVertical: 2,
        paddingHorizontal: 5,
        marginRight: 4,
        marginBottom: 2,
    },
    badgePillBlue: {
        backgroundColor: '#e8f0fa',
        borderWidth: 1,
        borderColor: '#93b4d8',
        borderRadius: 2,
        paddingVertical: 2,
        paddingHorizontal: 5,
        marginRight: 4,
        marginBottom: 2,
    },
    badgePillTextAmber: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#92400e',
    },
    badgePillTextBlue: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#1e3a5f',
    },
    badgePillGreen: {
        backgroundColor: '#e8eee9',
        borderWidth: 1,
        borderColor: '#2C3E2F',
        borderRadius: 2,
        paddingVertical: 2,
        paddingHorizontal: 5,
        marginRight: 4,
        marginBottom: 2,
    },
    badgePillTextGreen: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#2C3E2F',
    },
    structureBody: {
        padding: 8,
    },
    // Flag/warning
    flagRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 6,
        paddingVertical: 3,
        backgroundColor: '#fffbeb',
        borderWidth: 1,
        borderColor: '#fcd34d',
        borderRadius: 2,
        marginBottom: 4,
    },
    flagText: {
        fontSize: 7,
        color: '#92400e',
    },
    // Footer
    footer: {
        position: 'absolute',
        bottom: 15,
        left: 30,
        right: 30,
        fontSize: 7,
        color: '#999999',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    // Global costs
    globalCostRow: {
        flexDirection: 'row',
        paddingVertical: 2,
        paddingHorizontal: 6,
        borderBottomWidth: 0.5,
        borderBottomColor: '#e5e7eb',
    },
    // Notes
    notesBox: {
        backgroundColor: '#f8fafc',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 3,
        padding: 6,
        marginTop: 4,
    },
    notesText: {
        fontSize: 8,
        color: '#475569',
        fontStyle: 'italic',
        textDecoration: 'none',
    },
    chooseOneNote: {
        backgroundColor: '#eef4f0',
        borderWidth: 1,
        borderColor: '#2C3E2F',
        borderRadius: 3,
        padding: 6,
        marginBottom: 12,
    },
    chooseOneNoteText: {
        fontSize: 7.5,
        color: '#1e3a2f',
        lineHeight: 1.4,
    },
});

// ============================================================================
// HELPERS
// ============================================================================

const fmt = (v: number | undefined): string => {
    if (v === undefined || v === null) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.round(v));
};

const pct = (v: number): string => `${v.toFixed(1)}%`;

/** Ensure no inherited line-through from parent Text chains in @react-pdf/renderer */
const PDF_TEXT_NONE: { textDecoration: 'none' } = { textDecoration: 'none' };

const ratio = (numerator: number, denominator: number): number => {
    if (denominator === 0) return 0;
    return (numerator / denominator) * 100;
};

const formatReviewLine = (line: ReviewTextLine): string => {
    const head = line.detail ? `${line.label}: ${line.detail}` : line.label;
    if (line.amount == null) return head;
    return `${head} = ${fmt(line.amount)}`;
};

// ============================================================================
// COMPONENT
// ============================================================================

const InternalReviewPdfDocument: React.FC<{ data: InternalReviewData }> = ({ data }) => {
    const {
        clientName,
        projectTitle,
        preparedBy,
        proposalDate,
        nodeSets,
        profitMargin,
        globalCosts,
        isKitProposal,
        activeSpecies,
        status,
        designEstimating,
        rates,
        joineryPeople,
    } = data;

    const exclusiveStaff = computeExclusiveStaffEconomics(nodeSets, profitMargin, globalCosts, {
        isKitProposal,
        activeSpecies,
        designEstimating,
        designEstimatingRates: rates,
        proposalStatus: status,
    });
    const pricedNodeSets = exclusiveStaff.defaultNodeSets;
    const defaultExclusiveName = exclusiveStaff.choices[0]?.name;
    const estimatingDesignRaw = estimatingRaw(designEstimating, rates, status);

    // ── Aggregate totals on the default Choose one pool ──
    const totals = pricedNodeSets.reduce((acc, ns) => {
        const qty = ns.quantity || 1;
        return {
            design: acc.design + ns.design.total * qty,
            production: acc.production + ns.production.total * qty,
            timber: acc.timber + ns.materials.timberTotal * qty,
            materials: acc.materials + (ns.materials.total - ns.materials.timberTotal) * qty,
            freight: acc.freight + ns.freight.total * qty,
            overhead: acc.overhead + ns.overhead.total * qty,
            install: acc.install + ns.install.total * qty,
            travel: acc.travel + ns.travel.total * qty,
            boardFeet: acc.boardFeet + reviewShopBoardFeet(ns) * qty,
            installDays: acc.installDays + (ns.install.days || 0) * qty,
            designHours: acc.designHours + structureDesignHours(ns.design) * qty,
        };
    }, { design: 0, production: 0, timber: 0, materials: 0, freight: 0, overhead: 0, install: 0, travel: 0, boardFeet: 0, installDays: 0, designHours: 0 });
    const globalCostTotal = (globalCosts?.totalGlobalCosts || 0) + estimatingDesignRaw;
    const globalSplitEligibleCount = countGlobalCostShareEligible(pricedNodeSets);
    const perStructureGlobalDollarShare =
        globalSplitEligibleCount > 0 ? globalCostTotal / globalSplitEligibleCount : 0;
    const structureRawSubtotal =
        totals.design +
        totals.production +
        totals.timber +
        totals.materials +
        totals.freight +
        totals.overhead +
        totals.install +
        totals.travel;
    const poPct = getEffectiveProjectOverheadPercent(globalCosts);
    const baseForProjectOverhead = structureRawSubtotal + globalCostTotal;
    const projectOverheadAmount =
        poPct > 0 ? Math.round(baseForProjectOverhead * (poPct / 100)) : 0;
    /** Equal split of project % OH across enabled structure cards (same as billable timber kit in ProposalBuilder). */
    const enabledStructureCardCount = pricedNodeSets.length || 1;
    const projectOverheadSharePerStructure =
        poPct > 0 && projectOverheadAmount > 0
            ? Math.round(projectOverheadAmount / enabledStructureCardCount)
            : 0;
    const rawTotal = baseForProjectOverhead + projectOverheadAmount;
    const billableTotal = Math.round(applyProfitMargin(rawTotal, profitMargin));
    const dollarMargin = billableTotal - rawTotal;
    const effectiveMargin = ratio(dollarMargin, billableTotal);
    const costToRevenueRatio = ratio(rawTotal, billableTotal);
    const totalStructures = pricedNodeSets.reduce((sum, ns) => sum + (ns.quantity || 1), 0);
    const avgRevenuePerStructure = totalStructures > 0 ? billableTotal / totalStructures : 0;

    // Variable costs (timber + materials + freight)
    const variableCosts = totals.timber + totals.materials + totals.freight;
    const contributionMargin = billableTotal - Math.round(applyProfitMargin(variableCosts, profitMargin));

    // Efficiency metrics
    const costPerBoardFoot = totals.boardFeet > 0 ? totals.timber / totals.boardFeet : 0;
    const revenuePerInstallDay = totals.installDays > 0 ? billableTotal / totals.installDays : 0;
    const laborCosts = totals.design + totals.production + totals.install;
    const laborCostRatio = ratio(laborCosts, rawTotal);
    /** Structure overhead node + global $ lines + project-level overhead % (all as % of billable). */
    const overheadBurdenRate = ratio(
        totals.overhead + globalCostTotal + projectOverheadAmount,
        billableTotal
    );

    // Risk indicators
    const materialExposure = ratio(totals.timber + totals.materials, rawTotal);
    const logisticsBurden = ratio(totals.freight + totals.travel + globalCostTotal, rawTotal);
    const damagedTimberBudget = globalCosts?.damagedTimberBudget?.enabled ? globalCosts.damagedTimberBudget.amount : 0;
    const contingencyCoverage = totals.timber > 0 ? ratio(damagedTimberBudget, totals.timber) : 0;

    // Revenue mix
    const billableDesign = Math.round(applyProfitMargin(totals.design, profitMargin));
    const billableTimberKit = Math.round(
        applyProfitMargin(
            totals.timber + totals.materials + totals.production + totals.freight + totals.overhead + globalCostTotal,
            profitMargin
        )
    );
    const billableInstall = Math.round(applyProfitMargin(totals.install, profitMargin));
    const billableTravel = Math.round(applyProfitMargin(totals.travel, profitMargin));

    // Break-even margin (where profit = $0, i.e., margin = 0%)
    // The break-even point is where billable = rawCost, which means margin = 0%
    // More useful: what margin % makes this project lose ≤X%?
    // For now just show the minimum margin to break even = 0%
    const breakEvenNote = 'Margin must be > 0% to be profitable';

    /** Per-structure overhead nodes + project-wide % OH (single Overhead line; project % is not in Global $ row). */
    const overheadRowRaw = totals.overhead + projectOverheadAmount;

    // Cost breakdown rows (categoryNote only where we need a subcaption)
    const costRows: Array<{ label: string; raw: number; categoryNote?: string }> = [
        { label: 'Design', raw: totals.design },
        { label: 'Production', raw: totals.production },
        { label: 'Timber', raw: totals.timber },
        { label: 'Other Materials', raw: totals.materials },
        { label: 'Freight', raw: totals.freight },
        {
            label: 'Overhead',
            raw: overheadRowRaw,
            ...(poPct > 0 && projectOverheadAmount > 0
                ? {
                      categoryNote: `Includes ${poPct}% project-wide OH (${fmt(projectOverheadAmount)} raw). Not included in Global Project Costs (dollar lines only).`,
                  }
                : {}),
        },
        { label: 'Install', raw: totals.install },
        { label: 'Travel', raw: totals.travel },
    ];

    if (globalCostTotal > 0) {
        costRows.push({
            label: 'Global Project Costs',
            raw: globalCostTotal,
            categoryNote: 'Enabled global dollar lines only — does not include % overhead above.',
        });
    }

    // Per-structure calculations
    const structureDetails = nodeSets.filter(ns => ns.enabled !== false).map(ns => {
        const qty = ns.quantity || 1;
        const structureRaw = ns.design.total + ns.production.total + (ns.materials.total) + ns.freight.total + ns.overhead.total + ns.install.total + ns.travel.total;
        const structureRawWithQty = structureRaw * qty;
        const structureBillable = Math.round(applyProfitMargin(structureRawWithQty, profitMargin));
        const structureMargin = structureBillable - structureRawWithQty;

        return {
            ...ns,
            /** Explicit so per-structure PDF rows always see the flags (Firestore / spread edge cases). */
            excludeInstall: !!ns.excludeInstall,
            excludeFromGlobalPrices: !!ns.excludeFromGlobalPrices,
            clientOptional: !!ns.clientOptional,
            clientExclusive: structureIsExclusive(ns),
            qty,
            structureRaw,
            structureRawWithQty,
            structureBillable,
            structureMargin,
            marginPct: ratio(structureMargin, structureBillable),
        };
    });

    // Flags
    const flags: string[] = [];
    nodeSets.filter(ns => ns.enabled !== false).forEach(ns => {
        if (ns.excludeInstall) {
            flags.push(`ℹ ${ns.name}: Install & travel excluded from client pricing`);
        } else {
            if (ns.install.total === 0) flags.push(`⚠ ${ns.name}: Install = $0 (not included)`);
            if (ns.travel.total === 0) flags.push(`⚠ ${ns.name}: Travel = $0`);
        }
        if (ns.freight.total === 0 && data.isKitProposal && !usesGlobalStructureCosting(data)) {
            flags.push(`⚠ ${ns.name}: Freight = $0`);
        }
    });
    const exclusiveStructures = listExclusiveStructures(nodeSets);
    if (exclusiveStructures.length > 1) {
        const rangeNote = exclusiveStaff.billableRange
            ? ` Billable range ${fmt(exclusiveStaff.billableRange.min)}–${fmt(exclusiveStaff.billableRange.max)}.`
            : '';
        flags.push(
            `ℹ Choose one: project totals use ${defaultExclusiveName || exclusiveStructures[0].name} (first in builder order). Client purchases exactly one of ${exclusiveStructures.map(ns => ns.name).join(', ')}.${rangeNote} Unselected exclusive structures are not in the project total.`,
        );
    }
    if (effectiveMargin < 30) flags.push(`🚩 Low effective margin: ${pct(effectiveMargin)}`);
    if (overheadBurdenRate > 20) flags.push(`🚩 High overhead burden: ${pct(overheadBurdenRate)} of revenue`);

    const dateStr = proposalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Global cost items for detail
    const globalCostItems = globalCosts ? [
        { key: 'baselineTravel', label: 'Baseline Travel' },
        { key: 'additionalTravel', label: 'Additional Travel' },
        { key: 'globalFreightInbound', label: 'Freight — Inbound' },
        { key: 'globalFreightOutbound', label: 'Freight — Outbound' },
        { key: 'equipmentRental', label: 'Equipment Rental' },
        { key: 'additionalGear', label: 'Additional Gear' },
        { key: 'ppeBudget', label: 'PPE Budget' },
        { key: 'jobsiteIncidentals', label: 'Jobsite Incidentals' },
        { key: 'additionalInsurance', label: 'Additional Insurance' },
        { key: 'damagedTimberBudget', label: 'Damaged Timber Budget' },
        { key: 'jobsiteSecurity', label: 'Jobsite Security' },
        { key: 'additionalVehicles', label: 'Additional Vehicles' },
        { key: 'jobSpecificTooling', label: 'Job-Specific Tooling' },
    ] : [];

    return (
        <Document>
            {/* ================================================================ */}
            {/* PAGE 1: EXECUTIVE SUMMARY                                        */}
            {/* ================================================================ */}
            <Page size="LETTER" style={s.page}>
                {/* Header */}
                <View style={s.header}>
                    <Text style={s.headerTitle}>Internal Financial Review</Text>
                    <Text style={s.headerSubtitle}>{clientName} — {projectTitle}</Text>
                    <View style={s.headerMeta}>
                        <Text>Date: {dateStr}</Text>
                        {preparedBy && <Text>Prepared by: {preparedBy}</Text>}
                        <Text>Status: {status || 'Draft'}</Text>
                        {isKitProposal && <Text>Kit Proposal{activeSpecies ? ` (${activeSpecies})` : ''}</Text>}
                    </View>
                </View>

                {/* Confidential Banner */}
                <View style={s.confidential}>
                    <Text style={s.confidentialText}>⚠ CONFIDENTIAL — INTERNAL USE ONLY — DO NOT DISTRIBUTE TO CLIENTS</Text>
                </View>

                {exclusiveStaff.choices.length >= 2 && (
                    <View style={s.chooseOneNote}>
                        <Text style={s.chooseOneNoteText}>
                            Choose one: project totals use {defaultExclusiveName}
                            {exclusiveStaff.billableRange
                                ? `. Billable range if the client picks another option: ${fmt(exclusiveStaff.billableRange.min)}–${fmt(exclusiveStaff.billableRange.max)}`
                                : ''}
                            . Unselected exclusive structures are listed on the detail pages and are not in the project total (no global / project-overhead share).
                        </Text>
                    </View>
                )}

                {/* ── Financial Snapshot ── */}
                <Text style={s.sectionTitle}>Financial Snapshot</Text>
                <View style={s.summaryRow}>
                    <View style={s.summaryBox}>
                        <Text style={s.summaryLabel}>Raw Cost</Text>
                        <Text style={s.summaryValue}>{fmt(rawTotal)}</Text>
                        <Text style={s.summarySubtext}>{totalStructures} structure{totalStructures !== 1 ? 's' : ''}</Text>
                    </View>
                    <View style={s.summaryBoxHighlight}>
                        <Text style={s.summaryLabelWhite}>Billable Total</Text>
                        <Text style={s.summaryValueWhite}>{fmt(billableTotal)}</Text>
                        <Text style={s.summarySubtextWhite}>{pct(profitMargin)} target margin</Text>
                    </View>
                    <View style={s.summaryBox}>
                        <Text style={s.summaryLabel}>Dollar Margin</Text>
                        <Text style={s.summaryValue}>{fmt(dollarMargin)}</Text>
                        <Text style={s.summarySubtext}>{pct(effectiveMargin)} effective</Text>
                    </View>
                    <View style={s.summaryBox}>
                        <Text style={s.summaryLabel}>Avg / Structure</Text>
                        <Text style={s.summaryValue}>{fmt(avgRevenuePerStructure)}</Text>
                        <Text style={s.summarySubtext}>billable revenue</Text>
                    </View>
                </View>

                {/* ── Cost Category Breakdown ── */}
                <Text style={s.sectionTitle}>Cost Category Breakdown</Text>
                <View style={s.tableHeader}>
                    <Text style={[s.tableHeaderText, { width: '40%' }]}>Category</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Raw Cost</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Billable</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>% of Total</Text>
                </View>
                {costRows.map((row, i) => (
                    <View key={i} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                        <View style={{ width: '40%', flexDirection: 'column', paddingRight: 4 }}>
                            <Text style={{ fontSize: 8 }}>{row.label}</Text>
                            {row.categoryNote ? (
                                <Text style={{ fontSize: 5.5, color: '#64748b', marginTop: 2, lineHeight: 1.35 }}>
                                    {row.categoryNote}
                                </Text>
                            ) : null}
                        </View>
                        <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>{fmt(row.raw)}</Text>
                        <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>{fmt(Math.round(applyProfitMargin(row.raw, profitMargin)))}</Text>
                        <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>{pct(ratio(row.raw, rawTotal))}</Text>
                    </View>
                ))}
                <View style={s.tableRowTotal}>
                    <Text style={[s.tableHeaderText, { width: '40%' }]}>TOTAL</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>{fmt(rawTotal)}</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>{fmt(billableTotal)}</Text>
                    <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>100%</Text>
                </View>

                {/* ── Profitability Metrics ── */}
                <View wrap={false}>
                <Text style={s.sectionTitle}>Profitability Metrics</Text>
                <View style={s.metricGrid}>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Gross Margin</Text>
                        <Text style={s.metricValue}>{pct(effectiveMargin)}</Text>
                        <Text style={s.metricSubtext}>{fmt(dollarMargin)}</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Contribution Margin</Text>
                        <Text style={s.metricValue}>{fmt(contributionMargin)}</Text>
                        <Text style={s.metricSubtext}>after variable costs</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Cost-to-Revenue</Text>
                        <Text style={s.metricValue}>{pct(costToRevenueRatio)}</Text>
                        <Text style={s.metricSubtext}>{breakEvenNote}</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Revenue Mix</Text>
                        <Text style={[s.metricValue, { fontSize: 8 }]}>
                            D:{pct(ratio(billableDesign, billableTotal))}  K:{pct(ratio(billableTimberKit, billableTotal))}
                        </Text>
                        <Text style={[s.metricSubtext, { fontSize: 6 }]}>
                            I:{pct(ratio(billableInstall, billableTotal))}  T:{pct(ratio(billableTravel, billableTotal))}
                        </Text>
                    </View>
                </View>
                </View>

                {/* ── Efficiency Ratios ── */}
                <View wrap={false}>
                <Text style={s.sectionTitle}>Efficiency Ratios</Text>
                <View style={s.metricGrid}>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Cost / Board Foot</Text>
                        <Text style={s.metricValue}>${costPerBoardFoot.toFixed(2)}</Text>
                        <Text style={s.metricSubtext}>{totals.boardFeet.toLocaleString()} total BF</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Revenue / Install Day</Text>
                        <Text style={s.metricValue}>{fmt(revenuePerInstallDay)}</Text>
                        <Text style={s.metricSubtext}>{totals.installDays} total days</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Labor Cost Ratio</Text>
                        <Text style={s.metricValue}>{pct(laborCostRatio)}</Text>
                        <Text style={s.metricSubtext}>{fmt(laborCosts)} labor</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Overhead Burden</Text>
                        <Text style={s.metricValue}>{pct(overheadBurdenRate)}</Text>
                        <Text style={s.metricSubtext}>of revenue</Text>
                    </View>
                </View>
                </View>

                {/* ── Risk Indicators ── */}
                <View wrap={false}>
                <Text style={s.sectionTitle}>Risk Indicators</Text>
                <View style={s.metricGrid}>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Material Exposure</Text>
                        <Text style={s.metricValue}>{pct(materialExposure)}</Text>
                        <Text style={s.metricSubtext}>{fmt(totals.timber + totals.materials)}</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Logistics Burden</Text>
                        <Text style={s.metricValue}>{pct(logisticsBurden)}</Text>
                        <Text style={s.metricSubtext}>freight + travel + global</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Contingency</Text>
                        <Text style={s.metricValue}>{pct(contingencyCoverage)}</Text>
                        <Text style={s.metricSubtext}>{fmt(damagedTimberBudget)} budget</Text>
                    </View>
                    <View style={s.metricCard} wrap={false}>
                        <Text style={s.metricLabel}>Design Hours</Text>
                        <Text style={s.metricValue}>{totals.designHours}h</Text>
                        <Text style={s.metricSubtext}>{fmt(totals.design)} cost</Text>
                    </View>
                </View>
                </View>


                {/* Footer */}
                <View style={s.footer} fixed>
                    <Text>Leiper's Creek Timber Frames — Internal Review</Text>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
                </View>
            </Page>

            {/* ================================================================ */}
            {/* PAGE 2+: PER-STRUCTURE DETAIL                                     */}
            {/* ================================================================ */}
            <Page size="LETTER" style={[s.page, PDF_TEXT_NONE]} wrap>
                {/* Header */}
                <View style={s.header}>
                    <Text style={s.headerTitle}>Structure Detail — {clientName}</Text>
                    <Text style={s.headerSubtitle}>{projectTitle}</Text>
                </View>

                {rates && listEstimatingAuditLines(designEstimating, rates, status).length > 0 && (
                    <View style={{ marginBottom: 10 }}>
                        <Text style={{ ...PDF_TEXT_NONE, fontSize: 8, fontWeight: 'bold', color: '#475569', marginBottom: 3 }}>
                            Estimating / modeling (project overhead)
                        </Text>
                        {listEstimatingAuditLines(designEstimating, rates, status).map((line) => (
                            <Text key={line.key} style={{ ...PDF_TEXT_NONE, fontSize: 7, color: '#64748b' }}>
                                {line.label}: {line.detail} = {fmt(line.amount)}
                            </Text>
                        ))}
                        <Text style={{ ...PDF_TEXT_NONE, fontSize: 7, color: '#1e293b', marginTop: 2 }}>
                            Total: {fmt(estimatingDesignRaw)}
                        </Text>
                    </View>
                )}

                {structureDetails.map((sd) => {
                    const inferredNoInstallFromBillable =
                        !!sd.billablePrices &&
                        sd.billablePrices.install === 0 &&
                        sd.billablePrices.travel === 0 &&
                        (sd.install.total > 0 || sd.travel.total > 0);
                    const showNoGlobalBadge = !!sd.excludeFromGlobalPrices;
                    const showNoInstallBadge = !!sd.excludeInstall || inferredNoInstallFromBillable;
                    const showClientOptionalBadge = !!(sd as { clientOptional?: boolean }).clientOptional;
                    const showClientExclusiveBadge = !!(sd as { clientExclusive?: boolean }).clientExclusive;
                    const inDefaultPool = pricedNodeSets.some(ns => ns.id === sd.id);
                    const unselectedExclusive = showClientExclusiveBadge && !inDefaultPool;

                    const stH = { ...PDF_TEXT_NONE, fontSize: 8, fontWeight: 'bold' as const, color: '#475569', marginBottom: 3 };
                    const stD = { ...PDF_TEXT_NONE, fontSize: 7, color: '#64748b' };
                    const stT = { ...PDF_TEXT_NONE, fontSize: 7, color: '#1e293b', marginBottom: 4 };
                    const stTL = { ...PDF_TEXT_NONE, fontSize: 7, color: '#1e293b' };
                    const hardwareLines = listMaterialsHardwareReviewLines(sd.materials);
                    const materialsOther = materialsOtherResidual(sd.materials, hardwareLines);
                    const shopBf = reviewShopBoardFeet(sd);

                    return (
                    <View key={sd.id} style={s.structureBox}>
                        {/* Structure Header */}
                        <View style={s.structureHeader}>
                            <View style={{ flex: 1, flexDirection: 'column' }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={s.structureName}>{sd.name}</Text>
                                    {sd.qty > 1 && <Text style={[s.structureQty, { marginLeft: 6 }]}>×{sd.qty}</Text>}
                                </View>
                                {(showNoGlobalBadge || showNoInstallBadge || showClientOptionalBadge || showClientExclusiveBadge) && (
                                    <View style={s.structureBadgeRow}>
                                        {showNoGlobalBadge && (
                                            <View style={s.badgePillAmber}>
                                                <Text style={s.badgePillTextAmber}>No global share</Text>
                                            </View>
                                        )}
                                        {showClientOptionalBadge && (
                                            <View style={s.badgePillAmber}>
                                                <Text style={s.badgePillTextAmber}>Client optional</Text>
                                            </View>
                                        )}
                                        {showClientExclusiveBadge && (
                                            <View style={s.badgePillGreen}>
                                                <Text style={s.badgePillTextGreen}>
                                                    {unselectedExclusive ? 'Choose 1 — not in project total' : 'Choose 1'}
                                                </Text>
                                            </View>
                                        )}
                                        {showNoInstallBadge && (
                                            <View style={s.badgePillBlue}>
                                                <Text style={s.badgePillTextBlue}>No Install</Text>
                                            </View>
                                        )}
                                    </View>
                                )}
                            </View>
                            <View style={{ flexDirection: 'row', flexShrink: 0, marginLeft: 8 }}>
                                <Text style={{ ...PDF_TEXT_NONE, fontSize: 8, color: '#64748b', marginRight: 10 }}>Raw: {fmt(sd.structureRawWithQty)}</Text>
                                <Text style={{ ...PDF_TEXT_NONE, fontSize: 8, fontWeight: 'bold', color: '#1e3a2f', marginRight: 10 }}>Billable: {fmt(sd.structureBillable)}</Text>
                                <Text style={{ ...PDF_TEXT_NONE, fontSize: 8, color: '#64748b' }}>Margin: {pct(sd.marginPct)}</Text>
                            </View>
                        </View>

                        <View style={s.structureBody}>
                            {/* Cost node details — 2 column layout */}
                            <View style={{ flexDirection: 'row' }}>
                                {/* Left column */}
                                <View style={{ width: '50%', paddingRight: 6 }}>
                                    <Text style={stH}>Design</Text>
                                    {listJoineryReviewLines(sd.design, rates, joineryPeople).map((line) => (
                                        <Text key={line.key} style={stD}>{formatReviewLine(line)}</Text>
                                    ))}
                                    <Text style={stT}>Total: {fmt(sd.design.total)}</Text>

                                    <Text style={stH}>Production</Text>
                                    <Text style={stD}>{reviewShopBoardFeetLabel(sd)}</Text>
                                    {sd.materials.quoteMultipleSpecies &&
                                        (sd.materials.alternateSpecies?.length ?? 0) > 0 &&
                                        speciesProductionAuditLines({
                                            ...sd,
                                            production: { ...sd.production, boardFeet: shopBf },
                                        }).map((line) => {
                                            const label = line.isDefault
                                                ? `Default (${line.speciesName})`
                                                : line.speciesName;
                                            const deltaNote = line.isDefault
                                                ? ''
                                                : line.productionDiff === 0
                                                    ? '   (same as default)'
                                                    : line.productionDiff > 0
                                                        ? `   (+${fmt(line.productionDiff)} raw)`
                                                        : `   (${fmt(line.productionDiff)} raw)`;
                                            return (
                                                <Text key={`${sd.id}-${line.speciesName}`} style={stD}>
                                                    {label}: ${line.ratePerBdft.toFixed(2)}/BF × {line.boardFeet.toLocaleString()} = {fmt(line.raw)}{deltaNote}
                                                </Text>
                                            );
                                        })}
                                    <Text style={stT}>
                                        {sd.materials.quoteMultipleSpecies && (sd.materials.alternateSpecies?.length ?? 0) > 0
                                            ? `Total (default): ${fmt(sd.production.total)}`
                                            : `Total: ${fmt(sd.production.total)}`}
                                    </Text>

                                    <Text style={stH}>Materials</Text>
                                    {sd.materials.quoteMultipleSpecies &&
                                        (sd.materials.alternateSpecies?.length ?? 0) > 0 ? (
                                        <>
                                            {speciesTimberAuditLines(sd).map((line) => {
                                                const label = line.isDefault
                                                    ? `Default (${line.speciesName})`
                                                    : line.speciesName;
                                                const deltaNote = line.isDefault
                                                    ? ''
                                                    : line.timberDiff === 0
                                                        ? '   (same as default)'
                                                        : line.timberDiff > 0
                                                            ? `   (+${fmt(line.timberDiff)} raw)`
                                                            : `   (${fmt(line.timberDiff)} raw)`;
                                                return (
                                                    <Text key={`${sd.id}-timber-${line.speciesName}`} style={stD}>
                                                        {label}: {fmt(line.timberTotal)}{deltaNote}
                                                    </Text>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <Text style={stD}>Timber: {fmt(sd.materials.timberTotal)}</Text>
                                    )}
                                    {hardwareLines.map((line) => (
                                        <Text key={line.key} style={stD}>{formatReviewLine(line)}</Text>
                                    ))}
                                    {materialsOther > 0 && (
                                        <Text style={stD}>Other: {fmt(materialsOther)}</Text>
                                    )}
                                    <Text style={stT}>
                                        {sd.materials.quoteMultipleSpecies && (sd.materials.alternateSpecies?.length ?? 0) > 0
                                            ? `Total (default): ${fmt(sd.materials.total)}`
                                            : `Total: ${fmt(sd.materials.total)}`}
                                    </Text>

                                    <Text style={stH}>Freight</Text>
                                    <Text style={stD}>To Aspers: {sd.freight.milesToAspers}mi  |  To Site: {sd.freight.milesToJobSite}mi</Text>
                                    <Text style={stTL}>Total: {fmt(sd.freight.total)}</Text>
                                </View>

                                {/* Right column */}
                                <View style={{ width: '50%', paddingLeft: 6 }}>
                                    <Text style={stH}>Overhead</Text>
                                    <Text style={stD}>{sd.overhead.days} days</Text>
                                    <Text style={stT}>Total: {fmt(sd.overhead.total)}</Text>
                                    {poPct > 0 && projectOverheadAmount > 0 && inDefaultPool && (
                                        <View style={{ marginBottom: 6, padding: 5, backgroundColor: '#f8fafc', borderLeftWidth: 3, borderLeftColor: '#1e3a2f', borderRadius: 2 }}>
                                            <Text style={{ ...PDF_TEXT_NONE, fontSize: 6.5, fontWeight: 'bold', color: '#1e3a2f', marginBottom: 2 }}>
                                                Project-wide overhead ({poPct}% of project raw)
                                            </Text>
                                            <Text style={{ ...PDF_TEXT_NONE, fontSize: 6.5, color: '#475569', lineHeight: 1.35 }}>
                                                {fmt(projectOverheadSharePerStructure)} this structure’s share — total {fmt(projectOverheadAmount)} ÷ {enabledStructureCardCount} structure
                                                {enabledStructureCardCount !== 1 ? 's' : ''}. Applied in timber kit billable pricing, separate from the overhead node totals above.
                                            </Text>
                                        </View>
                                    )}

                                    <Text style={stH}>Install</Text>
                                    <Text style={stD}>On-site days: {sd.install.days}  |  Labor: {fmt(sd.install.teamLaborPerDay)}/day</Text>
                                    {listInstallLaborReviewLines(sd.install).map((line) => (
                                        <Text key={line.key} style={stD}>{formatReviewLine(line)}</Text>
                                    ))}
                                    {(Number(sd.install.fuelBudget) || 0) > 0 && (
                                        <Text style={stD}>Jobsite fuel: {fmt(sd.install.fuelBudget)}</Text>
                                    )}
                                    {listInstallEquipmentAuditLines(sd.install).map(row => (
                                        <Text key={row.label} style={stD}>{row.label}  = {fmt(row.amount)}</Text>
                                    ))}
                                    <Text style={stT}>Total: {fmt(sd.install.total)}</Text>
                                    {sd.install.overheadTotal > 0 && (
                                        <Text style={stD}>
                                            Includes install-day overhead add-on {fmt(sd.install.overheadTotal)} (install days × overhead daily rate), included in the total above.
                                        </Text>
                                    )}
                                    {sd.install.overheadTotal === 0 && sd.overhead.total > 0 && (
                                        <Text style={stD}>
                                            Structure overhead {fmt(sd.overhead.total)} is the row above (shop / structure days). With project-wide overhead percent off, it is part of client installation pricing together with labor and equipment. Install-day overhead add-on (rate × install days) is $0 here—that is separate from the structure overhead line.
                                        </Text>
                                    )}

                                    <Text style={stH}>Travel</Text>
                                    {(Number(sd.travel.milesToJobSite) || 0) > 0 && (
                                        <Text style={stD}>{sd.travel.milesToJobSite}mi one-way</Text>
                                    )}
                                    {listTravelReviewLines(
                                        sd.travel,
                                        sd.install.days,
                                        getInstallLaborLines(sd.install),
                                    ).map((line) => (
                                        <View key={line.key}>
                                            <Text style={stD}>{formatReviewLine(line)}</Text>
                                            {line.note ? <Text style={stD}>{line.note}</Text> : null}
                                        </View>
                                    ))}
                                    <Text style={stTL}>Total: {fmt(sd.travel.total)}</Text>
                                </View>
                            </View>

                            {/* Billable prices if available */}
                            {sd.billablePrices && (
                                <View style={{ marginTop: 4, paddingTop: 4, borderTopWidth: 0.5, borderTopColor: '#e5e7eb' }}>
                                    <Text style={{ ...PDF_TEXT_NONE, fontSize: 7, fontWeight: 'bold', color: '#1e3a2f' }}>
                                        Billable: Design {fmt(sd.billablePrices.design)} | Kit {fmt(sd.billablePrices.timberKit)} | Install {fmt(sd.billablePrices.install)} | Travel {fmt(sd.billablePrices.travel)} | Subtotal {fmt(sd.billablePrices.subtotal)}
                                    </Text>
                                </View>
                            )}

                            {/* Species options */}
                            {sd.billablePrices?.speciesOptions && Object.keys(sd.billablePrices.speciesOptions).length > 0 && (
                                <View style={{ marginTop: 3 }}>
                                    <Text style={{ ...PDF_TEXT_NONE, fontSize: 7, color: '#64748b' }}>
                                        Species: {Object.entries(sd.billablePrices.speciesOptions).map(([species, price]) => `${species}: ${fmt(price)}`).join(' | ')}
                                    </Text>
                                </View>
                            )}

                            {/* Notes */}
                            {sd.notes && (
                                <View style={s.notesBox}>
                                    <Text style={s.notesText}>Notes: {sd.notes}</Text>
                                </View>
                            )}
                        </View>
                    </View>
                    );
                })}

                {/* Footer */}
                <View style={s.footer} fixed>
                    <Text>Leiper's Creek Timber Frames — Internal Review</Text>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
                </View>
            </Page>

            {/* ================================================================ */}
            {/* PAGE 3: GLOBAL COSTS & FLAGS                                      */}
            {/* ================================================================ */}
            {(globalCostTotal > 0 || projectOverheadAmount > 0 || flags.length > 0) && (
                <Page size="LETTER" style={s.page}>
                    {/* Header */}
                    <View style={s.header}>
                        <Text style={s.headerTitle}>Global Costs & Flags — {clientName}</Text>
                        <Text style={s.headerSubtitle}>{projectTitle}</Text>
                    </View>

                    {/* Global Costs Detail */}
                    {globalCosts && globalCostTotal > 0 && (
                        <>
                            <Text style={s.sectionTitle}>Global Project Costs</Text>
                            <View style={s.summaryRow}>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>Total Global Costs</Text>
                                    <Text style={s.summaryValue}>{fmt(globalCostTotal)}</Text>
                                </View>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>Per structure (global $)</Text>
                                    <Text style={s.summaryValue}>{fmt(perStructureGlobalDollarShare)}</Text>
                                    <Text style={s.summarySubtext}>
                                        {globalSplitEligibleCount > 0
                                            ? `split among ${globalSplitEligibleCount} structure(s) in the global split; project overhead % uses all enabled structures`
                                            : 'no structures in global split'}
                                    </Text>
                                </View>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>% of Raw Total</Text>
                                    <Text style={s.summaryValue}>{pct(ratio(globalCostTotal, rawTotal))}</Text>
                                </View>
                            </View>

                            <View style={s.tableHeader}>
                                <Text style={[s.tableHeaderText, { width: '35%' }]}>Cost Item</Text>
                                <Text style={[s.tableHeaderText, { width: '15%', textAlign: 'center' }]}>Status</Text>
                                <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Amount</Text>
                                <Text style={[s.tableHeaderText, { width: '30%' }]}>Notes</Text>
                            </View>
                            {globalCostItems.map((item, i) => {
                                const costItem = (globalCosts as any)[item.key];
                                if (!costItem) return null;
                                return (
                                    <View key={i} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                                        <Text style={{ width: '35%', fontSize: 8 }}>{item.label}</Text>
                                        <Text style={{ width: '15%', textAlign: 'center', fontSize: 8, color: costItem.enabled ? '#16a34a' : '#94a3b8' }}>
                                            {costItem.enabled ? '✓ On' : '— Off'}
                                        </Text>
                                        <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>
                                            {costItem.enabled ? fmt(costItem.amount) : '—'}
                                        </Text>
                                        <Text style={{ width: '30%', fontSize: 7, color: '#64748b', paddingLeft: 4 }}>
                                            {costItem.notes || ''}
                                        </Text>
                                    </View>
                                );
                            })}
                        </>
                    )}

                    {projectOverheadAmount > 0 && (
                        <>
                            <Text style={s.sectionTitle}>Project Overhead (% of total)</Text>
                            <Text style={{ fontSize: 7, color: '#64748b', marginBottom: 6 }}>
                                Same project-wide portion as in the Overhead row on page 1 (not double-counted).
                            </Text>
                            <View style={s.summaryRow}>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>Rate</Text>
                                    <Text style={s.summaryValue}>{poPct}%</Text>
                                </View>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>Raw amount</Text>
                                    <Text style={s.summaryValue}>{fmt(projectOverheadAmount)}</Text>
                                    <Text style={s.summarySubtext}>structure + global raw before margin</Text>
                                </View>
                                <View style={s.summaryBox}>
                                    <Text style={s.summaryLabel}>% of raw total</Text>
                                    <Text style={s.summaryValue}>{pct(ratio(projectOverheadAmount, rawTotal))}</Text>
                                </View>
                            </View>
                        </>
                    )}

                    {/* Flags & Warnings */}
                    {flags.length > 0 && (
                        <>
                            <Text style={s.sectionTitle}>Flags & Warnings</Text>
                            {flags.map((flag, i) => (
                                <View key={i} style={s.flagRow}>
                                    <Text style={s.flagText}>{flag}</Text>
                                </View>
                            ))}
                        </>
                    )}

                    {/* Per-Structure Margin Comparison */}
                    <Text style={s.sectionTitle}>Per-Structure Margin Comparison</Text>
                    <View style={s.tableHeader}>
                        <Text style={[s.tableHeaderText, { width: '30%' }]}>Structure</Text>
                        <Text style={[s.tableHeaderText, { width: '10%', textAlign: 'center' }]}>Qty</Text>
                        <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Raw Cost</Text>
                        <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Billable</Text>
                        <Text style={[s.tableHeaderText, { width: '20%', textAlign: 'right' }]}>Margin</Text>
                    </View>
                    {structureDetails.map((sd, i) => (
                        <View key={sd.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                            <Text style={{ width: '30%', fontSize: 8 }}>
                                {sd.name}
                                {structureIsExclusive(sd) && !pricedNodeSets.some(ns => ns.id === sd.id)
                                    ? ' (Choose 1 — not in total)'
                                    : ''}
                            </Text>
                            <Text style={{ width: '10%', textAlign: 'center', fontSize: 8 }}>{sd.qty}</Text>
                            <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>{fmt(sd.structureRawWithQty)}</Text>
                            <Text style={{ width: '20%', textAlign: 'right', fontSize: 8 }}>{fmt(sd.structureBillable)}</Text>
                            <Text style={{ width: '20%', textAlign: 'right', fontSize: 8, color: sd.marginPct < 30 ? '#dc2626' : '#16a34a' }}>
                                {pct(sd.marginPct)} ({fmt(sd.structureMargin)})
                            </Text>
                        </View>
                    ))}

                    {/* Footer */}
                    <View style={s.footer} fixed>
                        <Text>Leiper's Creek Timber Frames — Internal Review</Text>
                        <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
                    </View>
                </Page>
            )}
        </Document>
    );
};

export default InternalReviewPdfDocument;
